import { useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import { useFarm } from "@/context/FarmContext";
import { NoFarm, PageHeader } from "@/components/Page";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  Activity,
  Beaker,
  CalendarDays,
  Check,
  CheckCircle2,
  Edit3,
  FlaskConical,
  Leaf,
  Plus,
  Save,
  Sprout,
  Trash2,
  X,
  Zap,
} from "lucide-react";

const EMPTY_FORM = {
  sample_date: new Date().toISOString().slice(0, 10),
  sample_name: "",
  paddock_id: "",
  laboratory: "",

  dry_matter_pct: "",
  me_mj_kg_dm: "",
  crude_protein_pct_dm: "",
  ndf_pct_dm: "",
  adf_pct_dm: "",
  sugar_pct_dm: "",
  starch_pct_dm: "",
  ash_pct_dm: "",
  dmd_pct: "",

  calcium_pct_dm: "",
  phosphorus_pct_dm: "",
  magnesium_pct_dm: "",
  potassium_pct_dm: "",
  sodium_pct_dm: "",
  sulphur_pct_dm: "",
  chloride_pct_dm: "",
  nitrate_mg_kg_dm: "",

  notes: "",
  active: true,
};

const NUMBER_FIELDS = [
  "dry_matter_pct",
  "me_mj_kg_dm",
  "crude_protein_pct_dm",
  "ndf_pct_dm",
  "adf_pct_dm",
  "sugar_pct_dm",
  "starch_pct_dm",
  "ash_pct_dm",
  "dmd_pct",
  "calcium_pct_dm",
  "phosphorus_pct_dm",
  "magnesium_pct_dm",
  "potassium_pct_dm",
  "sodium_pct_dm",
  "sulphur_pct_dm",
  "chloride_pct_dm",
  "nitrate_mg_kg_dm",
];

function toFormValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return value;
}

function preparePayload(form) {
  const payload = {
    ...form,
    sample_name: form.sample_name.trim() || null,
    paddock_id: form.paddock_id.trim() || null,
    laboratory: form.laboratory.trim() || null,
    notes: form.notes.trim() || null,
  };

  NUMBER_FIELDS.forEach((field) => {
    const value = form[field];

    payload[field] =
      value === "" || value === null || value === undefined
        ? null
        : Number(value);
  });

  return payload;
}

function formatValue(value, decimals = 1) {
  if (
    value === null ||
    value === undefined ||
    Number.isNaN(Number(value))
  ) {
    return "—";
  }

  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function NutrientMetric({
  icon: Icon,
  label,
  value,
  unit,
  accent = false,
}) {
  return (
    <Card
      className={`p-5 ${
        accent
          ? "border-l-4 border-l-primary bg-primary/[0.03]"
          : ""
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {label}
        </p>

        <Icon className="h-4 w-4 text-primary shrink-0" />
      </div>

      <p className="mt-3 font-display text-2xl font-extrabold text-[hsl(148,38%,16%)] tabular-nums">
        {formatValue(value)}

        <span className="ml-1 text-sm font-semibold text-muted-foreground">
          {unit}
        </span>
      </p>
    </Card>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  type = "number",
  step = "0.1",
  placeholder,
  required = false,
}) {
  return (
    <div>
      <Label htmlFor={name}>
        {label}
        {required ? " *" : ""}
      </Label>

      <Input
        id={name}
        name={name}
        type={type}
        step={type === "number" ? step : undefined}
        value={value}
        placeholder={placeholder}
        required={required}
        onChange={onChange}
        className="mt-1"
      />
    </div>
  );
}

export default function GrassQuality() {
  const { farmId, loading } = useFarm();
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [message, setMessage] = useState("");

  const {
    data,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["grass-quality", farmId],
    queryFn: async () =>
      (
        await api.get(
          `/farms/${farmId}/grass-quality`
        )
      ).data,
    enabled: !!farmId,
  });

  const analyses = useMemo(
    () =>
      Array.isArray(data?.analyses)
        ? data.analyses
        : [],
    [data]
  );

  const activeAnalysis =
    analyses.find((analysis) => analysis.active) ||
    null;

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["grass-quality", farmId],
    });

    await queryClient.invalidateQueries({
      queryKey: ["feed-budget", farmId],
    });

    await queryClient.invalidateQueries({
      queryKey: ["nutrition", farmId],
    });
  };

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      if (editingId) {
        return (
          await api.put(
            `/farms/${farmId}/grass-quality/${editingId}`,
            payload
          )
        ).data;
      }

      return (
        await api.post(
          `/farms/${farmId}/grass-quality`,
          payload
        )
      ).data;
    },

    onSuccess: async () => {
      setMessage(
        editingId
          ? "Grass analysis updated."
          : "Grass analysis saved."
      );

      setForm(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);

      await refresh();
    },

    onError: (error) => {
      setMessage(
        error?.response?.data?.detail ||
          "The grass analysis could not be saved."
      );
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (analysisId) =>
      (
        await api.post(
          `/farms/${farmId}/grass-quality/${analysisId}/activate`
        )
      ).data,

    onSuccess: async () => {
      setMessage(
        "The selected grass analysis is now active."
      );

      await refresh();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (analysisId) =>
      (
        await api.delete(
          `/farms/${farmId}/grass-quality/${analysisId}`
        )
      ).data,

    onSuccess: async () => {
      setMessage("Grass analysis deleted.");

      await refresh();
    },
  });

  if (!loading && !farmId) {
    return <NoFarm />;
  }

  if (isLoading) {
    return (
      <div className="text-muted-foreground">
        Loading grass analyses…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-muted-foreground">
        Grass-quality information is unavailable.
      </div>
    );
  }

  const handleChange = (event) => {
    const { name, value, type, checked } =
      event.target;

    setForm((current) => ({
      ...current,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setMessage("");
    setShowForm(true);
  };

  const startEdit = (analysis) => {
    setEditingId(analysis.id);

    setForm({
      sample_date:
        analysis.sample_date ||
        new Date().toISOString().slice(0, 10),

      sample_name: analysis.sample_name || "",
      paddock_id: analysis.paddock_id || "",
      laboratory: analysis.laboratory || "",

      dry_matter_pct: toFormValue(
        analysis.dry_matter_pct
      ),

      me_mj_kg_dm: toFormValue(
        analysis.me_mj_kg_dm
      ),

      crude_protein_pct_dm: toFormValue(
        analysis.crude_protein_pct_dm
      ),

      ndf_pct_dm: toFormValue(
        analysis.ndf_pct_dm
      ),

      adf_pct_dm: toFormValue(
        analysis.adf_pct_dm
      ),

      sugar_pct_dm: toFormValue(
        analysis.sugar_pct_dm
      ),

      starch_pct_dm: toFormValue(
        analysis.starch_pct_dm
      ),

      ash_pct_dm: toFormValue(
        analysis.ash_pct_dm
      ),

      dmd_pct: toFormValue(
        analysis.dmd_pct
      ),

      calcium_pct_dm: toFormValue(
        analysis.calcium_pct_dm
      ),

      phosphorus_pct_dm: toFormValue(
        analysis.phosphorus_pct_dm
      ),

      magnesium_pct_dm: toFormValue(
        analysis.magnesium_pct_dm
      ),

      potassium_pct_dm: toFormValue(
        analysis.potassium_pct_dm
      ),

      sodium_pct_dm: toFormValue(
        analysis.sodium_pct_dm
      ),

      sulphur_pct_dm: toFormValue(
        analysis.sulphur_pct_dm
      ),

      chloride_pct_dm: toFormValue(
        analysis.chloride_pct_dm
      ),

      nitrate_mg_kg_dm: toFormValue(
        analysis.nitrate_mg_kg_dm
      ),

      notes: analysis.notes || "",
      active: Boolean(analysis.active),
    });

    setMessage("");
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    setMessage("");

    saveMutation.mutate(preparePayload(form));
  };

  const handleDelete = (analysis) => {
    const confirmed = window.confirm(
      `Delete ${
        analysis.sample_name ||
        `the analysis from ${analysis.sample_date}`
      }?`
    );

    if (confirmed) {
      deleteMutation.mutate(analysis.id);
    }
  };

  return (
    <div>
      <PageHeader
        title="Grass Quality"
        subtitle="Record pasture analyses and select the result used in nutrition calculations."
        action={
          <Button
            type="button"
            onClick={startNew}
            className="rounded-full"
            data-testid="add-grass-analysis"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add analysis
          </Button>
        }
      />

      {message && (
        <Card className="mb-6 p-4 border-l-4 border-l-primary bg-primary/[0.03]">
          <p className="text-sm font-medium text-[hsl(148,38%,16%)]">
            {message}
          </p>
        </Card>
      )}

      {activeAnalysis ? (
        <Card
          className="p-6 mb-6 border-l-4 border-l-primary"
          data-testid="active-grass-analysis"
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />

                <h2 className="font-display text-xl font-bold text-[hsl(148,38%,16%)]">
                  Active Grass Analysis
                </h2>
              </div>

              <p className="mt-2 text-sm text-muted-foreground">
                {activeAnalysis.sample_name ||
                  "Pasture sample"}{" "}
                · sampled{" "}
                {new Date(
                  activeAnalysis.sample_date
                ).toLocaleDateString()}
              </p>

              {activeAnalysis.laboratory && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Laboratory:{" "}
                  {activeAnalysis.laboratory}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  startEdit(activeAnalysis)
                }
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-6">
            <NutrientMetric
              icon={Leaf}
              label="Dry Matter"
              value={activeAnalysis.dry_matter_pct}
              unit="%"
            />

            <NutrientMetric
              icon={Zap}
              label="ME"
              value={activeAnalysis.me_mj_kg_dm}
              unit="MJ/kg DM"
              accent
            />

            <NutrientMetric
              icon={Activity}
              label="Crude Protein"
              value={
                activeAnalysis.crude_protein_pct_dm
              }
              unit="% DM"
            />

            <NutrientMetric
              icon={Sprout}
              label="NDF"
              value={activeAnalysis.ndf_pct_dm}
              unit="% DM"
            />

            <NutrientMetric
              icon={FlaskConical}
              label="DMD"
              value={activeAnalysis.dmd_pct}
              unit="%"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <NutrientMetric
              icon={Beaker}
              label="ADF"
              value={activeAnalysis.adf_pct_dm}
              unit="% DM"
            />

            <NutrientMetric
              icon={Beaker}
              label="Sugar"
              value={activeAnalysis.sugar_pct_dm}
              unit="% DM"
            />

            <NutrientMetric
              icon={Beaker}
              label="Starch"
              value={activeAnalysis.starch_pct_dm}
              unit="% DM"
            />

            <NutrientMetric
              icon={Beaker}
              label="Ash"
              value={activeAnalysis.ash_pct_dm}
              unit="% DM"
            />
          </div>

          <div className="mt-5 rounded-lg bg-muted p-4">
            <p className="text-sm font-semibold text-[hsl(148,38%,16%)]">
              Nutrition link
            </p>

            <p className="mt-1 text-sm text-muted-foreground">
              This active analysis will be used
              when calculating pasture energy,
              protein and fibre supply.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-2">
            <Leaf className="h-5 w-5 text-primary" />

            <h2 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
              No active grass analysis
            </h2>
          </div>

          <p className="mt-3 text-sm text-muted-foreground">
            Add a laboratory result or manual
            pasture analysis to include grass
            quality in nutrition calculations.
          </p>
        </Card>
      )}

      {showForm && (
        <Card
          className="p-6 mb-6"
          data-testid="grass-quality-form"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-bold text-[hsl(148,38%,16%)]">
                {editingId
                  ? "Edit Grass Analysis"
                  : "Add Grass Analysis"}
              </h2>

              <p className="mt-1 text-sm text-muted-foreground">
                Enter values on a dry-matter basis
                unless otherwise stated.
              </p>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={cancelForm}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="mt-6 space-y-7"
          >
            <section>
              <h3 className="font-semibold text-[hsl(148,38%,16%)]">
                Sample information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
                <Field
                  label="Sample date"
                  name="sample_date"
                  type="date"
                  value={form.sample_date}
                  onChange={handleChange}
                  required
                />

                <Field
                  label="Sample name"
                  name="sample_name"
                  type="text"
                  value={form.sample_name}
                  onChange={handleChange}
                  placeholder="July grazing sample"
                />

                <Field
                  label="Paddock ID or name"
                  name="paddock_id"
                  type="text"
                  value={form.paddock_id}
                  onChange={handleChange}
                  placeholder="Whole farm or Paddock 4"
                />

                <Field
                  label="Laboratory"
                  name="laboratory"
                  type="text"
                  value={form.laboratory}
                  onChange={handleChange}
                  placeholder="Laboratory name"
                />
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-[hsl(148,38%,16%)]">
                Core nutritional analysis
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-3">
                <Field
                  label="Dry matter (%)"
                  name="dry_matter_pct"
                  value={form.dry_matter_pct}
                  onChange={handleChange}
                />

                <Field
                  label="ME (MJ/kg DM)"
                  name="me_mj_kg_dm"
                  value={form.me_mj_kg_dm}
                  onChange={handleChange}
                />

                <Field
                  label="Crude protein (% DM)"
                  name="crude_protein_pct_dm"
                  value={
                    form.crude_protein_pct_dm
                  }
                  onChange={handleChange}
                />

                <Field
                  label="NDF (% DM)"
                  name="ndf_pct_dm"
                  value={form.ndf_pct_dm}
                  onChange={handleChange}
                />

                <Field
                  label="ADF (% DM)"
                  name="adf_pct_dm"
                  value={form.adf_pct_dm}
                  onChange={handleChange}
                />

                <Field
                  label="Sugar/WSC (% DM)"
                  name="sugar_pct_dm"
                  value={form.sugar_pct_dm}
                  onChange={handleChange}
                />

                <Field
                  label="Starch (% DM)"
                  name="starch_pct_dm"
                  value={form.starch_pct_dm}
                  onChange={handleChange}
                />

                <Field
                  label="Ash (% DM)"
                  name="ash_pct_dm"
                  value={form.ash_pct_dm}
                  onChange={handleChange}
                />

                <Field
                  label="DMD/OMD (%)"
                  name="dmd_pct"
                  value={form.dmd_pct}
                  onChange={handleChange}
                />
              </div>
            </section>

            <section>
              <h3 className="font-semibold text-[hsl(148,38%,16%)]">
                Minerals and safety
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                <Field
                  label="Calcium (% DM)"
                  name="calcium_pct_dm"
                  value={form.calcium_pct_dm}
                  onChange={handleChange}
                  step="0.01"
                />

                <Field
                  label="Phosphorus (% DM)"
                  name="phosphorus_pct_dm"
                  value={form.phosphorus_pct_dm}
                  onChange={handleChange}
                  step="0.01"
                />

                <Field
                  label="Magnesium (% DM)"
                  name="magnesium_pct_dm"
                  value={form.magnesium_pct_dm}
                  onChange={handleChange}
                  step="0.01"
                />

                <Field
                  label="Potassium (% DM)"
                  name="potassium_pct_dm"
                  value={form.potassium_pct_dm}
                  onChange={handleChange}
                  step="0.01"
                />

                <Field
                  label="Sodium (% DM)"
                  name="sodium_pct_dm"
                  value={form.sodium_pct_dm}
                  onChange={handleChange}
                  step="0.01"
                />

                <Field
                  label="Sulphur (% DM)"
                  name="sulphur_pct_dm"
                  value={form.sulphur_pct_dm}
                  onChange={handleChange}
                  step="0.01"
                />

                <Field
                  label="Chloride (% DM)"
                  name="chloride_pct_dm"
                  value={form.chloride_pct_dm}
                  onChange={handleChange}
                  step="0.01"
                />

                <Field
                  label="Nitrate (mg/kg DM)"
                  name="nitrate_mg_kg_dm"
                  value={form.nitrate_mg_kg_dm}
                  onChange={handleChange}
                  step="1"
                />
              </div>
            </section>

            <section>
              <Label htmlFor="notes">
                Notes
              </Label>

              <textarea
                id="notes"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={4}
                className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Sample conditions, grazing stage, fertiliser history or interpretation notes."
              />
            </section>

            <label className="flex items-center gap-3 rounded-lg border border-border p-4">
              <input
                type="checkbox"
                name="active"
                checked={form.active}
                onChange={handleChange}
                className="h-4 w-4"
              />

              <span>
                <span className="block text-sm font-semibold text-[hsl(148,38%,16%)]">
                  Use as active analysis
                </span>

                <span className="block text-xs text-muted-foreground">
                  The active result will be used
                  for pasture nutrient calculations.
                </span>
              </span>
            </label>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={cancelForm}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={saveMutation.isPending}
              >
                <Save className="h-4 w-4 mr-2" />

                {saveMutation.isPending
                  ? "Saving…"
                  : "Save analysis"}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary" />

            <h2 className="font-display text-xl font-bold text-[hsl(148,38%,16%)]">
              Analysis History
            </h2>
          </div>
        </div>

        {analyses.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">
            No grass analyses have been saved.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {analyses.map((analysis) => (
              <div
                key={analysis.id}
                className="p-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
              >
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-[hsl(148,38%,16%)]">
                      {analysis.sample_name ||
                        "Grass analysis"}
                    </p>

                    {analysis.active && (
                      <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                        <Check className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    )}
                  </div>

                  <p className="mt-1 text-sm text-muted-foreground">
                    {new Date(
                      analysis.sample_date
                    ).toLocaleDateString()}
                    {analysis.laboratory
                      ? ` · ${analysis.laboratory}`
                      : ""}
                  </p>

                  <p className="mt-2 text-sm text-muted-foreground">
                    ME{" "}
                    <strong>
                      {formatValue(
                        analysis.me_mj_kg_dm
                      )}
                    </strong>{" "}
                    MJ/kg DM · CP{" "}
                    <strong>
                      {formatValue(
                        analysis.crude_protein_pct_dm
                      )}
                    </strong>
                    % · NDF{" "}
                    <strong>
                      {formatValue(
                        analysis.ndf_pct_dm
                      )}
                    </strong>
                    %
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {!analysis.active && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={
                        activateMutation.isPending
                      }
                      onClick={() =>
                        activateMutation.mutate(
                          analysis.id
                        )
                      }
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Activate
                    </Button>
                  )}

                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      startEdit(analysis)
                    }
                  >
                    <Edit3 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={
                      deleteMutation.isPending
                    }
                    onClick={() =>
                      handleDelete(analysis)
                    }
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}